import { motion, useReducedMotion } from "framer-motion";

const ease = "easeOut";
const vp = { once: true, margin: "-20%" };

// Each beat reveals as it scrolls into view — scroll position drives the
// pacing, so no beat ever sits blank on a timer.
const Beat = ({ reduce, className, children, testid }) => (
  <motion.div
    data-testid={testid}
    initial={reduce ? false : { opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={vp}
    transition={{ duration: 0.85, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

const THOUGHTS = [
  "Did I create this habit?",
  "Should I change something?",
];

export const EmotionalTurn = () => {
  const reduce = useReducedMotion();

  return (
    <section id="the-turn" data-testid="emotional-turn-section" className="relative bg-inkdeep text-ivory overflow-hidden">
      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-28 lg:py-44">
        <div className="relative max-w-3xl">

          {/* BEAT 1 — the moment */}
          <Beat reduce={reduce}>
            <p className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-ivory">
              <span className="block">You finally sit down</span>
              <span className="block">after bedtime.</span>
            </p>
          </Beat>
          <Beat reduce={reduce} className="mt-6">
            <p className="font-serif font-light text-2xl sm:text-3xl leading-snug text-ivory/55">
              Twenty minutes later, you hear them again.
            </p>
          </Beat>

          {/* BEAT 2 — 2:13 a.m., from inside it */}
          <Beat reduce={reduce} className="mt-12 lg:mt-16 max-w-xl">
            <p className="text-base sm:text-lg font-normal leading-relaxed text-ivory/70">
              It&rsquo;s 2:13 a.m. You hear them again.
              I know — you just got back into bed.
            </p>
            <p className="mt-4 text-base sm:text-lg font-normal leading-relaxed text-ivory/70">
              So you lie there for a second and listen. Maybe they&rsquo;ll settle.
            </p>
            <p className="mt-4 text-base sm:text-lg font-normal leading-relaxed text-ivory/70">
              Then you hear them again. Okay. Do you give them another minute?
              Do you go in? Are they hungry? If you feed them, are you making
              tomorrow night harder?
            </p>
          </Beat>

          {/* BEAT 3 — Andrea's first answer is smaller than the panic */}
          <Beat reduce={reduce} className="mt-14 lg:mt-20 max-w-xl">
            <p className="text-base sm:text-lg font-normal leading-relaxed text-ivory/55">
              This is usually where I&rsquo;d tell you:
            </p>
            <p className="mt-5 font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-ivory">
              <span className="block">Don&rsquo;t try to figure out</span>
              <span className="block">the next six months</span>
              <span className="block">right now.</span>
            </p>
            <p className="mt-6 font-serif italic font-light text-2xl sm:text-3xl leading-snug text-clay">
              Let&rsquo;s just look at tonight.
            </p>
          </Beat>
          <Beat reduce={reduce} className="mt-10">
            <p className="font-serif font-light text-xl sm:text-2xl leading-snug text-ivory/55">
              Because the hard part usually isn&rsquo;t just being awake.
            </p>
            <p className="mt-3 font-serif font-light text-2xl sm:text-3xl leading-snug text-ivory">
              <em className="text-clay">It&rsquo;s not knowing what to do</em>
              <span className="block">when you are.</span>
            </p>
          </Beat>

          {/* BEAT 4 — the thoughts */}
          <div className="mt-12 lg:mt-16 space-y-4">
            {THOUGHTS.map((t, i) => (
              <Beat key={t} reduce={reduce}>
                <p className="font-serif italic font-light text-xl sm:text-2xl leading-snug text-ivory/65">
                  {t}
                </p>
              </Beat>
            ))}
            <Beat reduce={reduce}>
              <p className="font-serif italic font-light text-2xl sm:text-3xl leading-snug text-clay">
                Am I making it worse?
              </p>
            </Beat>
          </div>

          {/* BEAT 5 — discovery before change */}
          <Beat reduce={reduce} className="mt-16 lg:mt-24">
            <p className="font-serif font-light text-2xl sm:text-3xl lg:text-4xl leading-[1.2] tracking-tight text-ivory/80">
              <span className="block">If you asked me that,</span>
              <span className="block">I wouldn&rsquo;t change</span>
              <span className="block">everything yet.</span>
            </p>
            <p className="mt-5 font-serif italic font-light text-xl sm:text-2xl leading-snug text-clay">
              I&rsquo;d want to understand why this is happening first.
            </p>
          </Beat>

          {/* BEAT 6 — the turn */}
          <Beat reduce={reduce} className="mt-16 lg:mt-24">
            <p className="font-serif font-light text-2xl sm:text-3xl leading-snug text-ivory/55">
              <span className="block">You don&rsquo;t need another</span>
              <span className="block">answer from someone</span>
              <span className="block">who doesn&rsquo;t know</span>
              <span className="block">your child.</span>
            </p>
          </Beat>
          <Beat reduce={reduce} className="mt-8" testid="turn-final">
            <p className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-ivory">
              <span className="block">You need someone who can</span>
              <span className="block">look at <em className="text-clay">what&rsquo;s actually</em></span>
              <span className="block italic text-clay">happening.</span>
            </p>
          </Beat>

          {/* BEAT 7 — expertise shown through what Andrea notices */}
          <Beat reduce={reduce} className="mt-16 lg:mt-24 max-w-xl" testid="turn-noticing">
            <p className="text-base sm:text-lg font-normal leading-relaxed text-ivory/55">
              Sometimes a parent mentions it almost in passing:
            </p>
            <p className="mt-4 font-serif italic font-light text-xl sm:text-2xl leading-snug text-ivory/70">
              &ldquo;She stopped taking her second nap a few weeks ago.&rdquo;
            </p>
            <p className="mt-6 font-serif font-light text-2xl sm:text-3xl leading-snug text-ivory">
              Okay. <span className="italic text-clay">Let&rsquo;s talk about that.</span>
            </p>
            <p className="mt-6 text-base sm:text-lg font-normal leading-relaxed text-ivory/55">
              Because a detail that seems unrelated can sometimes explain why
              bedtime suddenly changed.
            </p>
          </Beat>
        </div>
      </div>
    </section>
  );
};
